import React, { useEffect, useState, useCallback, useRef } from "react";
import { useApp } from "../context/AppContext";
import api, { API } from "../api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash2, Download, HardDrive, Pencil, SquareKanban } from "lucide-react";

export default function Vault() {
  const { t, entity } = useApp();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [docType, setDocType] = useState("identity");
  const [expiry, setExpiry] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const [editDoc, setEditDoc] = useState(null);
  const [editForm, setEditForm] = useState({ subject_name: "", doc_type: "other", expiry_date: "" });
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/vault?entity_id=${entity}&q=${encodeURIComponent(q)}`).then((r) => setItems(r.data)).catch(() => {});
  }, [entity, q]);
  useEffect(() => { load(); }, [load]);

  const openEdit = (d) => {
    setEditForm({ subject_name: d.subject_name || "", doc_type: d.doc_type || "other", expiry_date: d.expiry_date || "" });
    setEditDoc(d);
  };

  const saveEdit = async () => {
    try {
      await api.put(`/vault/${editDoc.id}`, editForm);
      toast.success(t("msg.saved"));
      setEditDoc(null);
      load();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("entity_id", entity);
      fd.append("subject_name", subject);
      fd.append("doc_type", docType);
      fd.append("expiry_date", expiry);
      fd.append("file", file);
      await api.post("/vault/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("msg.saved"));
      setOpen(false);
      setSubject(""); setFile(null); setExpiry("");
      load();
    } catch {
      toast.error(t("msg.error"));
    } finally {
      setUploading(false);
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/files/download?path=${encodeURIComponent(doc.storage_path)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "document";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("msg.error"));
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/vault/${id}`); toast.success(t("msg.deleted")); load(); }
    catch { toast.error(t("msg.error")); }
  };

  const createTask = async (d) => {
    try {
      const r = await api.post("/tasks/from-vault-doc", { vault_id: d.id });
      toast.success(r.data.existing ? t("inv.taskExists") : t("inv.taskCreated"));
    } catch (e) { toast.error(e.response?.data?.detail || t("msg.error")); }
  };

  const typeLabel = (d) => ({ identity: t("vault.identity"), passport: t("vault.passport"), taxcode: t("vault.taxcode"),
    visura: t("vault.visura"), durc: t("vault.durc"), contract: t("vault.contract") }[d] || t("vault.other"));

  return (
    <div className="space-y-6" data-testid="vault-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("vault.title")}</h1>
        <div className="flex items-center gap-2">
          <Input className="w-44 sm:w-64" placeholder={t("vault.search")} value={q}
                 onChange={(e) => setQ(e.target.value)} data-testid="vault-search-input" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="vault-upload-button"><Plus className="h-4 w-4 mr-2" />{t("vault.upload")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("vault.upload")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>{t("vault.subject")}</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="vault-subject-input" /></div>
              <div><Label>{t("vault.docType")}</Label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger data-testid="vault-doctype-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="identity">{t("vault.identity")}</SelectItem>
                    <SelectItem value="passport">{t("vault.passport")}</SelectItem>
                    <SelectItem value="taxcode">{t("vault.taxcode")}</SelectItem>
                    <SelectItem value="visura">{t("vault.visura")}</SelectItem>
                    <SelectItem value="durc">{t("vault.durc")}</SelectItem>
                    <SelectItem value="contract">{t("vault.contract")}</SelectItem>
                    <SelectItem value="other">{t("vault.other")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t("vault.expiry")}</Label>
                <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="vault-expiry-input" /></div>
              <div>
                <Label>File</Label>
                <Input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files[0])} data-testid="vault-file-input" />
              </div>
              <Button className="w-full" onClick={submit} disabled={uploading || !file} data-testid="vault-submit-button">
                {uploading ? t("common.loading") : t("common.upload")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={!!editDoc} onOpenChange={(v) => !v && setEditDoc(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("vault.edit")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>{t("vault.subject")}</Label>
                <Input value={editForm.subject_name} onChange={(e) => setEditForm({ ...editForm, subject_name: e.target.value })} data-testid="vault-edit-subject-input" /></div>
              <div><Label>{t("vault.docType")}</Label>
                <Select value={editForm.doc_type} onValueChange={(v) => setEditForm({ ...editForm, doc_type: v })}>
                  <SelectTrigger data-testid="vault-edit-doctype-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="identity">{t("vault.identity")}</SelectItem>
                    <SelectItem value="passport">{t("vault.passport")}</SelectItem>
                    <SelectItem value="taxcode">{t("vault.taxcode")}</SelectItem>
                    <SelectItem value="visura">{t("vault.visura")}</SelectItem>
                    <SelectItem value="durc">{t("vault.durc")}</SelectItem>
                    <SelectItem value="contract">{t("vault.contract")}</SelectItem>
                    <SelectItem value="other">{t("vault.other")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t("vault.expiry")}</Label>
                <Input type="date" value={editForm.expiry_date} onChange={(e) => setEditForm({ ...editForm, expiry_date: e.target.value })} data-testid="vault-edit-expiry-input" /></div>
              <Button className="w-full" onClick={saveEdit} data-testid="vault-edit-save-button">{t("common.save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">{t("common.noData")}</div> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("vault.subject")}</TableHead>
                  <TableHead>{t("vault.docType")}</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>{t("common.date")}</TableHead>
                  <TableHead>{t("vault.expiry")}</TableHead>
                  <TableHead>{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((d) => (
                  <TableRow key={d.id} data-testid={`vault-row-${d.id}`}>
                    <TableCell className="font-medium">{d.subject_name || "—"}</TableCell>
                    <TableCell><Badge variant="secondary">{typeLabel(d.doc_type)}</Badge></TableCell>
                    <TableCell className="text-sm">{d.original_filename}</TableCell>
                    <TableCell className="font-mono text-xs">{(d.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell>
                      {d.expiry_date ? (
                        <Badge variant={d.expiry_date < new Date().toISOString().slice(0, 10) ? "destructive" : "secondary"}
                               className="font-mono text-[10px]" data-testid={`expiry-badge-${d.id}`}>
                          {d.expiry_date}
                        </Badge>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" title={t("vault.edit")} data-testid={`edit-doc-${d.id}`} onClick={() => openEdit(d)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {d.drive_link && (
                          <a href={d.drive_link} target="_blank" rel="noopener noreferrer" title="Google Drive"
                             data-testid={`drive-link-${d.id}`}
                             className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-accent transition-colors">
                            <HardDrive className="h-4 w-4 text-primary" />
                          </a>
                        )}
                        {d.expiry_date && (
                          <Button size="icon" variant="ghost" title={t("vault.createTask")} data-testid={`vault-task-${d.id}`} onClick={() => createTask(d)}>
                            <SquareKanban className="h-4 w-4 text-emerald-500" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" data-testid={`download-doc-${d.id}`} onClick={() => download(d)}>
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" data-testid={`delete-doc-${d.id}`} onClick={() => remove(d.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
